import { useCallback, useSyncExternalStore } from "react";

const cookieStore = new EventTarget();

interface CookieOptions {
    days?: number;
    path?: string;
    isCrossDomain?: boolean; // Cookie shared between subdomains
}

export const setCookie = (name: string, value: string, options?: CookieOptions) => {
    const optionsWithDefaults = {
        days: 7,
        path: "/",
        ...options
    };

    const host = document.location.hostname;

    const expires = new Date(Date.now() + optionsWithDefaults.days * 864e5).toUTCString();
    const baseCookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=${optionsWithDefaults.path}`;
    // if isCrossDomain is true, the cookie will be set at the domain level (ie: for `apricot.sharegate.com`, the cookie will be set to `.sharegate.com`)
    if (optionsWithDefaults.isCrossDomain) {
        if (host.split(".").length === 1) {
            document.cookie = baseCookie;
        } else {
            const domainParts = host.split(".");
            domainParts.shift();
            document.cookie = `${baseCookie}; domain=.${domainParts.join(".")}`;
            if (getCookie(name) == null || getCookie(name) !== value) {
                document.cookie = `${baseCookie}; domain=.${host}`;
            }
        }
    } else {
        document.cookie = baseCookie;
    }

    cookieStore.dispatchEvent(new Event("change"));
};

const getCookie = (name: string, initialValue?: string) => {
    return (
        document.cookie.split("; ").reduce((r, v) => {
            const parts = v.split("=");

            return parts[0] === name ? decodeURIComponent(parts[1]) : r;
        }, "") || initialValue
    );
};

function subscribe(callback: () => void) {
    cookieStore.addEventListener("change", callback);

    return () => cookieStore.removeEventListener("change", callback);
}

export function useCookies(key: string, initialValue?: string) {
    const value = useSyncExternalStore(subscribe, () => getCookie(key, initialValue));

    const updateItem = useCallback(
        (newValue: string, options?: CookieOptions) => {
            setCookie(key, newValue, options);
        },
        [key]
    );

    return [value, updateItem] as const;
}
